import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faEnvelope,
  faPaperPlane,
  faPlus,
  faArrowLeft,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

interface Participant {
  userId: string;
  fullName: string;
  role: number;
}

interface ThreadSummary {
  threadId: string;
  subject: string;
  lastMessagePreview: string;
  lastMessageSenderName: string;
  lastMessageAt: string;
  hasUnread: boolean;
  participants: Participant[];
}

interface MessageItem {
  messageId: string;
  senderId: string;
  senderName: string;
  isOwnMessage: boolean;
  body: string;
  sentAt: string;
}

interface ThreadDetail {
  threadId: string;
  subject: string;
  createdAt: string;
  participants: Participant[];
  messages: MessageItem[];
}

interface RecipientOption {
  userId: string;
  fullName: string;
  role: number;
}

export default function MessagesPage() {
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [selectedThread, setSelectedThread] = useState<ThreadDetail | null>(null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(true);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showMobileThread, setShowMobileThread] = useState(false);

  // Compose form state
  const [recipients, setRecipients] = useState<RecipientOption[]>([]);
  const [composeRecipientId, setComposeRecipientId] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [isCreatingThread, setIsCreatingThread] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    fetchThreads();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedThread]);

  const fetchThreads = async () => {
    try {
      setIsLoadingThreads(true);
      setError(null);
      const response = await fetch('/api/messages', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to load messages');
      const data = await response.json();
      setThreads(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load your messages. Please try again.');
    } finally {
      setIsLoadingThreads(false);
    }
  };

  const openThread = async (threadId: string) => {
    try {
      setIsLoadingThread(true);
      setShowMobileThread(true);
      const response = await fetch(`/api/messages/${threadId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to load thread');
      const data = await response.json();
      setSelectedThread(data);

      // Mark as read and refresh thread list unread indicator
      await fetch(`/api/messages/${threadId}/mark-read`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setThreads((prev) =>
        prev.map((t) => (t.threadId === threadId ? { ...t, hasUnread: false } : t))
      );
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoadingThread(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedThread) return;

    try {
      setIsSending(true);
      const response = await fetch(`/api/messages/${selectedThread.threadId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ body: replyText }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      setSelectedThread(data.data);
      setReplyText('');
      fetchThreads();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSending(false);
    }
  };

  const openComposeModal = async () => {
    setShowComposeModal(true);
    try {
      const response = await fetch('/api/messages/recipients', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setRecipients(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateThread = async () => {
    if (!composeRecipientId || !composeSubject.trim() || !composeBody.trim()) return;

    try {
      setIsCreatingThread(true);
      const response = await fetch('/api/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          recipientId: composeRecipientId,
          subject: composeSubject,
          body: composeBody,
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');

      const data = await response.json();
      setShowComposeModal(false);
      setComposeRecipientId('');
      setComposeSubject('');
      setComposeBody('');
      await fetchThreads();
      setSelectedThread(data.data);
      setShowMobileThread(true);
    } catch (err) {
      console.error(err);
    } finally {
      setIsCreatingThread(false);
    }
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    return isToday
      ? date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' });
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-950">Messages</h1>
          <p className="text-sm text-brand-900/60 mt-1">
            Message your students, instructors, and administrators
          </p>
        </div>
        <button
          onClick={openComposeModal}
          className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 text-sm"
        >
          <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
          New Message
        </button>
      </div>

      <div className="p-8">
        {isLoadingThreads && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoadingThreads && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoadingThreads && !error && threads.length === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faEnvelope} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No messages yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              Start a conversation with your instructor or administrator.
            </p>
          </div>
        )}

        {!isLoadingThreads && !error && threads.length > 0 && (
          <div className="card-surface overflow-hidden" style={{ height: 'calc(100vh - 220px)' }}>
            <div className="flex h-full">
              {/* Thread list */}
              <div
                className={`w-full md:w-80 border-r border-canvas-200 overflow-y-auto flex-shrink-0 ${
                  showMobileThread ? 'hidden md:block' : 'block'
                }`}
              >
                {threads.map((thread) => (
                  <button
                    key={thread.threadId}
                    onClick={() => openThread(thread.threadId)}
                    className={`w-full text-left px-5 py-4 border-b border-canvas-100 hover:bg-canvas-50 transition-colors ${
                      selectedThread?.threadId === thread.threadId ? 'bg-brand-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p
                        className={`text-sm truncate ${
                          thread.hasUnread ? 'font-bold text-brand-950' : 'font-semibold text-brand-900/80'
                        }`}
                      >
                        {thread.subject}
                      </p>
                      {thread.hasUnread && (
                        <span className="w-2 h-2 rounded-full bg-brand-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-brand-900/50 mt-1 truncate">
                      {thread.lastMessageSenderName}: {thread.lastMessagePreview}
                    </p>
                    <p className="text-xs text-brand-900/40 mt-1">{formatTime(thread.lastMessageAt)}</p>
                  </button>
                ))}
              </div>

              {/* Thread detail */}
              <div
                className={`flex-1 flex flex-col ${
                  showMobileThread ? 'flex' : 'hidden md:flex'
                }`}
              >
                {isLoadingThread && (
                  <div className="flex-1 flex items-center justify-center">
                    <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
                  </div>
                )}

                {!isLoadingThread && !selectedThread && (
                  <div className="flex-1 flex items-center justify-center text-brand-900/40 text-sm">
                    Select a conversation to view messages
                  </div>
                )}

                {!isLoadingThread && selectedThread && (
                  <>
                    {/* Thread header */}
                    <div className="border-b border-canvas-200 px-5 py-4 flex items-center gap-3">
                      <button
                        onClick={() => setShowMobileThread(false)}
                        className="md:hidden p-1 text-brand-900"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
                      </button>
                      <div>
                        <h3 className="font-bold text-brand-950">{selectedThread.subject}</h3>
                        <p className="text-xs text-brand-900/50 mt-0.5">
                          {selectedThread.participants.map((p) => p.fullName).join(', ')}
                        </p>
                      </div>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-5 space-y-4">
                      {selectedThread.messages.map((message) => (
                        <div
                          key={message.messageId}
                          className={`flex ${message.isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                              message.isOwnMessage
                                ? 'bg-brand-gradient text-white'
                                : 'bg-canvas-100 text-brand-950'
                            }`}
                          >
                            {!message.isOwnMessage && (
                              <p className="text-xs font-semibold mb-1 opacity-70">
                                {message.senderName}
                              </p>
                            )}
                            <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
                            <p
                              className={`text-xs mt-1 ${
                                message.isOwnMessage ? 'text-white/70' : 'text-brand-900/40'
                              }`}
                            >
                              {formatTime(message.sentAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Reply box */}
                    <div className="border-t border-canvas-200 p-4 flex items-end gap-3">
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply();
                          }
                        }}
                        placeholder="Type a message..."
                        rows={1}
                        className="flex-1 px-4 py-2.5 border border-canvas-300 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                      />
                      <button
                        onClick={handleSendReply}
                        disabled={isSending || !replyText.trim()}
                        className="p-2.5 bg-brand-gradient text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex-shrink-0"
                      >
                        <FontAwesomeIcon
                          icon={isSending ? faSpinner : faPaperPlane}
                          className={`h-4 w-4 ${isSending ? 'animate-spin' : ''}`}
                        />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Compose Modal */}
      {showComposeModal && (
        <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-brand-950">New Message</h3>
              <button
                onClick={() => setShowComposeModal(false)}
                className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">To</label>
                <select
                  value={composeRecipientId}
                  onChange={(e) => setComposeRecipientId(e.target.value)}
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  <option value="">Select a recipient...</option>
                  {recipients.map((r) => (
                    <option key={r.userId} value={r.userId}>
                      {r.fullName} ({r.role === 0 ? 'Student' : r.role === 1 ? 'Instructor' : 'Admin'})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Subject</label>
                <input
                  type="text"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="What's this about?"
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-brand-950 mb-2">Message</label>
                <textarea
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  rows={4}
                  placeholder="Type your message..."
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowComposeModal(false)}
                className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateThread}
                disabled={
                  isCreatingThread || !composeRecipientId || !composeSubject.trim() || !composeBody.trim()
                }
                className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={isCreatingThread ? faSpinner : faPaperPlane} className={`h-3.5 w-3.5 ${isCreatingThread ? 'animate-spin' : ''}`} />
                {isCreatingThread ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
